import { remiApi } from "./remiApi";
import { toTeamMember, type IdxTeamMember } from "./teamMemberContract";

export interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  organization_id: string;
  photo_url?: string;
  bio?: string;
  slug?: string; // Optional slug field for unique identification
}

// The public IDX endpoint returns the whole org roster in one call, so the
// by-first-name / by-email lookups filter that list client-side rather than
// issuing a scoped query (there's no per-field endpoint).
export async function fetchTeamMembers(organizationId: string): Promise<TeamMember[]> {
  const { data } = await remiApi.get<{ team_members: IdxTeamMember[] }>(
    "/idx/team-members",
    { params: { org_id: organizationId } }
  );
  return (data.team_members ?? []).map((m) => toTeamMember(m, organizationId));
}

export const teamMemberService = {
  async getTeamMembersByOrganization(
    organizationId: string
  ): Promise<TeamMember[]> {
    return fetchTeamMembers(organizationId);
  },

  async getTeamMemberByFirstName(
    organizationId: string,
    firstName: string
  ): Promise<TeamMember | null> {
    const members = await fetchTeamMembers(organizationId);
    return members.find((m) => m.first_name === firstName) ?? null;
  },

  async getTeamMemberByEmail(
    organizationId: string,
    email: string
  ): Promise<TeamMember | null> {
    const members = await fetchTeamMembers(organizationId);
    const target = email.toLowerCase();
    return members.find((m) => m.email?.toLowerCase() === target) ?? null;
  },
};
